import React, { Component } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import configData from "../../config.json";

/**
 * HighchartsAdapter - Renders Highcharts chart based on chartConfig from widget instance.
 * Supports all 30+ chart types. Full implementation in Phase 7.
 *
 * Props:
 *   - config: Parsed widget instance config with chartConfig, apiEndpoint, etc.
 */
export default class HighchartsAdapter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      chartData: null,
      loading: true,
      sId: sessionStorage.getItem("sessionId"),
      uId: sessionStorage.getItem("uId"),
    };
  }

  componentDidMount() {
    this.fetchChartData();
  }

  componentDidUpdate(prevProps) {
    const prev = prevProps.pageFilters || {};
    const curr = this.props.pageFilters || {};
    if (JSON.stringify(prev) !== JSON.stringify(curr)) {
      this.setState({ loading: true });
      this.fetchChartData();
    }
  }

  fetchChartData = () => {
    const { config, pageFilters } = this.props;
    if (!config.apiEndPoint) {
      this.setState({ loading: false });
      return;
    }

    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);

    // Append extra API params (e.g., widgetId for widgetdefinition charts)
    if (config.apiParams) {
      const params = typeof config.apiParams === "object" ? config.apiParams : {};
      Object.entries(params).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    // Append runtime page filters (from SearchFilter widget).
    // Keys may carry a __operator suffix (e.g. createdOn__gte) — backend maps
    // these in widgetdefinition::get_widget_data().
    if (pageFilters && typeof pageFilters === "object") {
      Object.entries(pageFilters).forEach(([key, value]) => {
        if (value !== "" && value != null) {
          formData.append(key, value);
        }
      });
    }

    fetch(configData.api_url + config.apiEndPoint, {
      method: config.apiMethod || "POST",
      mode: "cors",
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "success") {
          this.setState({ chartData: data.list || [], loading: false });
        } else {
          this.setState({ loading: false });
        }
      })
      .catch((error) => {
        console.error("HighchartsAdapter fetch error:", error);
        this.setState({ loading: false });
      });
  };

  getChartTypeFromKey = (typeKey) => {
    if (!typeKey) return "bar";
    const mapping = {
      chart_bar: "bar",
      chart_line: "line",
      chart_pie: "pie",
      chart_doughnut: "pie",
      chart_funnel: "funnel",
      chart_gauge: "gauge",
      chart_solidgauge: "solidgauge",
      chart_stackedbar: "bar",
      chart_groupedbar: "bar",
      chart_variablepie: "variablepie",
      chart_bartarget: "bar",
      chart_multibartarget: "bar",
      chart_area: "area",
      chart_scatter: "scatter",
      chart_heatmap: "heatmap",
      chart_treemap: "treemap",
      chart_sunburst: "sunburst",
      chart_sankey: "sankey",
      chart_dependencywheel: "dependencywheel",
      chart_networkgraph: "networkgraph",
      chart_organization: "organization",
      chart_timeline: "timeline",
      chart_xrange: "xrange",
      chart_histogram: "histogram",
      chart_pareto: "pareto",
      chart_bullet: "bullet",
      chart_venn: "venn",
      chart_wordcloud: "wordcloud",
      chart_streamgraph: "streamgraph",
      chart_tilemap: "tilemap",
      chart_item: "item",
      chart_dumbbell: "dumbbell",
      chart_lollipop: "lollipop",
      chart_variwide: "variwide",
    };
    return mapping[typeKey] || "bar";
  };

  buildChartOptions = () => {
    const { config } = this.props;
    const { chartData } = this.state;
    const chartConfig = config.chartConfig || {};
    const chartType = this.getChartTypeFromKey(config.typeKey);

    // Merge default options with DB config
    const options = {
      chart: {
        type: chartType,
        ...(chartConfig.chart || {}),
      },
      title: {
        text: chartConfig.title || config.instanceName || "",
      },
      subtitle: {
        text: chartConfig.subtitle || "",
      },
      xAxis: chartConfig.xAxis || {},
      yAxis: chartConfig.yAxis || {},
      series: chartConfig.series || [],
      plotOptions: chartConfig.plotOptions || {},
      credits: { enabled: false },
      ...chartConfig.extra,
    };

    // If data was fetched and series mapping is configured, apply it
    if (chartData && chartConfig.dataMapping) {
      const mapping = chartConfig.dataMapping;
      if (mapping.type === "categories") {
        options.xAxis.categories = chartData.map((row) => row[mapping.categoryField]);
        options.series = (mapping.seriesFields || []).map((sf) => ({
          name: sf.name || sf.field,
          data: chartData.map((row) => parseFloat(row[sf.field]) || 0),
        }));
      }
    }

    return options;
  };

  render() {
    const { loading } = this.state;

    if (loading) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
          <span className="text-muted">Loading chart...</span>
        </div>
      );
    }

    const options = this.buildChartOptions();

    return (
      <div className="widget-chart-container">
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    );
  }
}
